import { D100Icon } from "@/components/D100Icon";
import { DieTypes, RollTos, RollTypes, RollWiths } from "@/constants";
import { Context } from "@/context";
import { instrumentDiceRoll } from "@/helpers/bi/instrumentDiceRoll";
import { getPlatform } from "@/helpers/getPlatform";
import { rollDice } from "@/helpers/rollDice";
import { useDiceRollPrivacyStore } from "@/hooks/useDiceRollPrivacyStore";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import type {
  DieType,
  ObjectValues,
  RollDicePayload,
  RollKind,
  RollTo,
  RollType,
  RollWith,
} from "@/types";
import D10 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d10.svg";
import D12 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d12.svg";
import D20 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d20.svg";
import D4 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d4.svg";
import D6 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d6.svg";
import D8 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d8.svg";
import AngleDown from "@dndbeyond/fontawesome-cache/svgs/regular/angle-down.svg";
import AngleUp from "@dndbeyond/fontawesome-cache/svgs/regular/angle-up.svg";
import ArrowsRotate from "@dndbeyond/fontawesome-cache/svgs/regular/arrows-rotate.svg";
import Check from "@dndbeyond/fontawesome-cache/svgs/regular/check.svg";
import UserIcon from "@dndbeyond/fontawesome-cache/svgs/regular/user.svg";
import Users from "@dndbeyond/fontawesome-cache/svgs/regular/users.svg";
import { Button } from "@dndbeyond/ttui/components/Button";
import { ToggleSwitch } from "@dndbeyond/ttui/components/ToggleSwitch";
import clsx from "clsx";
import type { MouseEvent } from "react";
import {
  forwardRef,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
} from "react";
import { AdvantageIcon } from "./components/AdvantageIcon";
import { DisadvantageIcon } from "./components/DisadvantageIcon";
import { useFitTextToContainerWidth } from "./hooks/useFitTextToContainerWidth";
import styles from "./styles.module.css";

export interface RollContextMenuProps {
  style: CSSProperties;
  diceNotation: string;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  action: string;
  entityId: string;
  entityType: string;
  dmId?: string;
  gameId?: string;
  name?: string;
  rollKind?: RollKind;
  rollType?: RollType;
  setRollData?: Dispatch<SetStateAction<RollDicePayload["data"]>>;
}

const damageRollTypes = {
  Crit: "Crit Damage",
  Flat: "Flat Roll",
} as const;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DieTypeIcons: Record<DieType, any> = {
  d4: D4,
  d6: D6,
  d8: D8,
  d10: D10,
  d12: D12,
  d20: D20,
  d100: D100Icon,
};

type DamageRollType = ObjectValues<typeof damageRollTypes>;

export const RollContextMenu = forwardRef<HTMLDivElement, RollContextMenuProps>(
  (
    {
      style,
      diceNotation,
      setIsOpen,
      action,
      entityId,
      entityType,
      gameId,
      dmId,
      name,
      rollKind,
      rollType,
      setRollData,
    },
    ref,
  ) => {
    const kind = rollKind?.toLowerCase() || "";
    const isDamageRoll = rollType === RollTypes.Damage;
    const { isPddReady, hasPddErrored, env, user } = useContext(Context);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const userRollPrivacy = useDiceRollPrivacyStore((state) =>
      state.getDiceRollPrivacy(Number(user?.id)),
    );
    const { setRollToPrivacy } = useDiceRollPrivacyStore();
    const [damageRollType, setDamageRollType] = useState<
      DamageRollType | undefined
    >(isDamageRoll ? damageRollTypes.Flat : undefined);
    const [rollWith, setRollWith] = useState<RollWith | undefined>(
      !isDamageRoll
        ? kind === "advantage"
          ? RollWiths.adv
          : kind === "disadvantage"
            ? RollWiths.disadv
            : RollWiths.flat
        : undefined,
    );
    const [isAdjustDiceOn, setIsAdjustDiceOn] = useState(false);
    const diceNotationConstant = (
      diceNotation.match(/([+-]\s*\d+)/g)?.[0] || ""
    ).replace(/\s/g, "");
    const isDiceNotationOnlyConstant = !/\d+d\d+/.test(diceNotation);
    const [tempQuantity, setTempQuantity] = useState(
      Number(
        isDiceNotationOnlyConstant
          ? diceNotation
          : diceNotation.match(/\d+(?=d)/)?.[0],
      ),
    );
    const [tempDieType, setTempDieType] = useState<DieType | undefined>(
      isDiceNotationOnlyConstant
        ? undefined
        : (diceNotation.match(/d\d+/)?.[0] as DieType),
    );
    const isCritDamage = damageRollType === damageRollTypes.Crit;
    const effectiveQuantity = tempQuantity * (isCritDamage ? 2 : 1);
    const rollDiceNotation = `${effectiveQuantity}${isDiceNotationOnlyConstant ? "" : tempDieType}${isDiceNotationOnlyConstant ? "" : diceNotationConstant}`;
    const quantityStep = 1;
    const minQuantity = 1;
    const dieTypes = Object.values(DieTypes);
    const dieTypeIndex = tempDieType ? dieTypes.indexOf(tempDieType) : -1;
    const isDieTypeAtMin = dieTypeIndex <= 0;
    const isDieTypeAtMax = dieTypeIndex >= dieTypes.length - 1;
    const isQuantityAtMin = tempQuantity <= minQuantity;
    const DieTypeIcon = tempDieType ? DieTypeIcons[tempDieType] : undefined;
    const adjustDiceDisplay = `${tempQuantity}${tempDieType || ""}`;
    const {
      containerRef: adjustDiceDisplayContainerRef,
      textRef: adjustDiceDisplayTextRef,
    } = useFitTextToContainerWidth(adjustDiceDisplay, {
      deps: [isAdjustDiceOn],
    });
    const rollButtonText = `Roll ${rollDiceNotation}`;
    const {
      containerRef: rollButtonTextContainerRef,
      textRef: rollButtonTextRef,
    } = useFitTextToContainerWidth(rollButtonText, {
      maxFontPx: 20,
      minFontPx: 12,
    });
    const setId = useUserDiceDataStore(
      (state) => state.getUserDiceData(Number(user?.id))?.setId,
    );
    const handleRollPrivacyClick = (newRollTo: RollTo) => {
      if (userRollPrivacy?.rollTo === newRollTo) return;

      setRollToPrivacy(Number(user?.id));
    };
    const handleDamageRollTypeClick = (newDamageRollType: DamageRollType) => {
      if (damageRollType === newDamageRollType) return;

      setDamageRollType(newDamageRollType);
    };
    const handleRollWithClick = (newRollWith: RollWith) => {
      if (rollWith === newRollWith) return;

      setRollWith(newRollWith);
    };
    const handleQuantityChange = (newQuantity: number) => {
      setTempQuantity(newQuantity);
    };
    const handleDieTypeChange = (increment?: boolean) => {
      const currentIndex = dieTypeIndex;
      const isIncrement = increment === undefined ? true : increment;
      const newIndex = isIncrement
        ? Math.min(currentIndex + 1, dieTypes.length - 1)
        : Math.max(currentIndex - 1, 0);
      const newDieType = dieTypes[newIndex] as DieType;

      setTempDieType(newDieType);
    };
    const handleRollButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setIsOpen(false);

      const { data } = rollDice(
        {
          diceNotation: rollDiceNotation,
          action,
          entityId,
          entityType,
          setId: String(setId),
          avatarUrl: user?.avatarUrl,
          dmId,
          gameId,
          name,
          rollKind: rollWith ? (rollWith as RollKind) : rollKind,
          rollType,
          userId: String(user?.id),
        },
        hasPddErrored,
        isPddReady,
        (payload) => {
          instrumentDiceRoll(
            user!,
            payload,
            getPlatform(),
            "rollContextMenu",
            gameId ? Number(gameId) : undefined,
            undefined,
            env,
          );
        },
      );

      setRollData?.(data);
    };

    useLayoutEffect(() => {
      const el = menuRef.current;

      if (!el) return;

      if (el.dataset.widthLocked === "1") return;

      const width = el.getBoundingClientRect().width;

      if (!width || width <= 0) return;

      el.style.width = `${Math.ceil(width)}px`;
      el.dataset.widthLocked = "1";
    }, []);

    return (
      <div
        ref={(node) => {
          menuRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        style={style}
        className={styles.menu}
      >
        <span className={styles.heading}>Send To:</span>
        <ul className={styles.list}>
          {Object.values(RollTos)
            .reverse()
            .map((rollToOption) => (
              <li key={rollToOption} className={styles.listItem}>
                <button
                  type="button"
                  className={styles.listItemButton}
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleRollPrivacyClick(rollToOption);
                  }}
                >
                  {rollToOption === RollTos.Self ? (
                    <UserIcon className={styles.svg} />
                  ) : (
                    <Users className={styles.svg} />
                  )}
                  {rollToOption}
                  {userRollPrivacy?.rollTo === rollToOption && (
                    <Check className={clsx([styles.svg, styles.check])} />
                  )}
                </button>
              </li>
            ))}
        </ul>
        <span className={styles.heading}>
          Roll {isDamageRoll ? "As" : "With"}:
        </span>
        {isDamageRoll ? (
          <>
            <ul className={styles.list}>
              {Object.values(damageRollTypes).map((damageRollTypeOption) => (
                <li key={damageRollTypeOption} className={styles.listItem}>
                  <button
                    type="button"
                    className={clsx([
                      styles.listItemButton,
                      styles.dmgRollOption,
                    ])}
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      handleDamageRollTypeClick(damageRollTypeOption);
                    }}
                  >
                    {damageRollTypeOption}
                    {damageRollType === damageRollTypeOption && (
                      <Check className={clsx([styles.svg, styles.check])} />
                    )}
                  </button>
                </li>
              ))}
            </ul>
            {!isDiceNotationOnlyConstant && (
              <section className={styles.section}>
                <div className={styles.adjustDiceHeader}>
                  <ArrowsRotate
                    className={clsx([styles.svg, styles.rotateIcon])}
                  />
                  <span className={styles.adjustDiceText}>Adjust Dice</span>
                  <div className={styles.toggleSwitchWrapper}>
                    <ToggleSwitch
                      id="adjust-dice-toggle"
                      isOn={isAdjustDiceOn}
                      label=""
                      onToggle={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        const baseQuantity =
                          Number(diceNotation.match(/\d+(?=d)/)?.[0]) || 1;
                        const nextDieType = diceNotation.match(
                          /d\d+/,
                        )?.[0] as DieType;

                        setTempQuantity(baseQuantity);
                        setTempDieType(nextDieType);
                        setIsAdjustDiceOn(!isAdjustDiceOn);
                      }}
                    />
                  </div>
                </div>
                {isAdjustDiceOn && (
                  <div className={styles.diceAdjustmentContainer}>
                    <div className={styles.adjustmentColumn}>
                      <Button
                        size="x-small"
                        className={styles.adjustDiceButton}
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          handleQuantityChange(tempQuantity + quantityStep);
                        }}
                      >
                        <AngleUp />
                      </Button>
                      <span className={styles.adjustDiceColumnText}>
                        Quantity
                      </span>
                      <Button
                        size="x-small"
                        className={clsx([
                          styles.adjustDiceButton,
                          isQuantityAtMin && styles.disabled,
                        ])}
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          handleQuantityChange(
                            Math.max(tempQuantity - quantityStep, minQuantity),
                          );
                        }}
                      >
                        <AngleDown />
                      </Button>
                    </div>
                    <div
                      ref={adjustDiceDisplayContainerRef}
                      className={styles.adjustDiceTempSlot}
                    >
                      <span
                        ref={adjustDiceDisplayTextRef}
                        className={styles.adjustDiceTempText}
                      >
                        {adjustDiceDisplay}
                      </span>
                    </div>
                    <div className={styles.adjustmentColumn}>
                      <Button
                        size="x-small"
                        className={clsx([
                          styles.adjustDiceButton,
                          isDieTypeAtMax && styles.disabled,
                        ])}
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          handleDieTypeChange();
                        }}
                      >
                        <AngleUp />
                      </Button>
                      <span className={styles.adjustDiceColumnText}>
                        Die Type
                      </span>
                      <Button
                        size="x-small"
                        className={clsx([
                          styles.adjustDiceButton,
                          isDieTypeAtMin && styles.disabled,
                        ])}
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          handleDieTypeChange(false);
                        }}
                      >
                        <AngleDown />
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            )}
          </>
        ) : (
          <ul className={styles.list}>
            {Object.values(RollWiths).map((rw) => (
              <li key={rw} className={styles.listItem}>
                <button
                  type="button"
                  className={styles.listItemButton}
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleRollWithClick(rw);
                  }}
                >
                  {rw === RollWiths.adv ? (
                    <AdvantageIcon className={styles.svg} />
                  ) : rw === RollWiths.disadv ? (
                    <DisadvantageIcon className={styles.svg} />
                  ) : (
                    <D20 className={styles.svg} />
                  )}
                  {rw}
                  {rw === rollWith && (
                    <Check className={clsx([styles.svg, styles.check])} />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          className={styles.rollButton}
          onClick={handleRollButtonClick}
        >
          {isDiceNotationOnlyConstant ? undefined : rollWith ? (
            rollWith === RollWiths.adv ? (
              <AdvantageIcon className={styles.svg} />
            ) : rollWith === RollWiths.disadv ? (
              <DisadvantageIcon className={styles.svg} />
            ) : (
              <DieTypeIcon className={clsx([styles.svg, styles.dieIcon])} />
            )
          ) : (
            <DieTypeIcon className={clsx([styles.svg, styles.dieIcon])} />
          )}
          <span
            ref={rollButtonTextContainerRef}
            className={styles.rollButtonTextSlot}
          >
            <span ref={rollButtonTextRef} className={styles.rollButtonText}>
              Roll{" "}
              <span className={styles.diceNotation}>{rollDiceNotation}</span>
            </span>
          </span>
        </button>
      </div>
    );
  },
);
RollContextMenu.displayName = "RollContextMenu";
